<template>
  <div class="backdrop" @click.self="$emit('close')">
    <div class="modal">
      <button class="x" @click="$emit('close')" aria-label="Close">×</button>

      <!-- Account header -->
      <div
        class="top"
        :class="{ topLink: canOpenAccountDetail }"
        :role="canOpenAccountDetail ? 'button' : undefined"
        :tabindex="canOpenAccountDetail ? 0 : undefined"
        :title="canOpenAccountDetail ? 'Open account details' : undefined"
        @click="openAccountDetail"
        @keydown.enter.prevent="openAccountDetail"
        @keydown.space.prevent="openAccountDetail"
      >
        <FavIcon :domain="signal?.account_domain || ''" :size="38" />
        <div class="head">
          <div class="accountTitle">{{ accountName }}</div>
          <div class="signalKindRow">
            <img :src="gmailFavicon" class="gmailIcon" alt="Gmail" />
            <span class="signalKindLabel">{{ kindLabel }}</span>
            <span class="directionBadge" :class="isOutbound ? 'out' : 'in'">
              {{ isOutbound ? 'Sent' : 'Received' }}
            </span>
          </div>
        </div>
      </div>

      <!-- Email address block -->
      <div class="emailShell">
        <div class="addressBlock">
          <div class="fieldRow">
            <div class="fieldKey">From</div>
            <div class="fieldValue">
              <span class="emailAddr">{{ fromDisplay }}</span>
            </div>
          </div>
          <div class="fieldRow">
            <div class="fieldKey">To</div>
            <div class="fieldValue">
              <span class="emailAddr">{{ toDisplay }}</span>
            </div>
          </div>
          <div v-if="ccDisplay" class="fieldRow">
            <div class="fieldKey">Cc</div>
            <div class="fieldValue">
              <span class="emailAddr">{{ ccDisplay }}</span>
            </div>
          </div>
          <div class="fieldRow">
            <div class="fieldKey">Date</div>
            <div class="fieldValue muted">{{ formatTs(signal?.occurred_at) }}</div>
          </div>
        </div>

        <!-- Message block -->
        <div class="messageBlock">
          <div class="subjectLine">{{ subjectDisplay }}</div>
          <div class="bodyText">{{ bodyDisplay }}</div>
        </div>
      </div>

      <!-- Trigger runs -->
      <div v-if="Number(signal?.triggered_count || 0) > 0" class="section">
        <div class="label">Triggers fired</div>
        <div class="value">
          <div v-if="triggerRunsLoading" class="muted">Loading…</div>
          <div v-else-if="triggerRunsError" class="muted">{{ triggerRunsError }}</div>
          <div v-else class="triggerRuns">
            <div v-for="r in triggerRuns" :key="r.run_id" class="triggerRun" @click="openRun(r.run_id)">
              <div class="triggerRunTop" :title="triggerRunTitle(r)">
                <div class="triggerNamePill">
                  <HeroIcon name="BoltIcon" iconStyle="solid" :size="14" color="#7c3aed" />
                  <span class="triggerNameText">{{ r.trigger_name || 'Trigger' }}</span>
                </div>
                <span v-if="r.agent_name" class="triggerArrow">→</span>
                <div v-if="r.agent_name" class="triggerAgent">
                  <HeroIcon
                    v-if="triggerAgentIcon(r)?.icon_name"
                    :name="triggerAgentIcon(r)?.icon_name"
                    :iconStyle="triggerAgentIcon(r)?.icon_style"
                    :color="triggerAgentIcon(r)?.icon_color"
                    :size="16"
                  />
                  <span class="triggerAgentName">{{ r.agent_name }}</span>
                </div>
                <button class="btn viewRunBtn" @click.stop="openRun(r.run_id)">View Agent Run</button>
              </div>
              <div v-if="r.created_at" class="muted">{{ formatTs(r.created_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="footer">
        <div class="left"></div>
        <div class="right">
          <button class="btn" @click="$emit('close')">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import FavIcon from './FavIcon.vue'
import HeroIcon from './HeroIcon.vue'
import { apiFetch } from '../services/api.ts'
import { googleFaviconUrl } from '../services/favicons.ts'

const props = defineProps({
  signal: { type: Object, required: true },
  accountIdByDomain: { type: Object, default: () => ({}) },
  agentById: { type: Object, default: () => ({}) },
})

defineEmits(['close'])

const router = useRouter()

const gmailFavicon = googleFaviconUrl('google.com', { size: 32 })

const isOutbound = computed(() => {
  const d = props.signal?.description
  return d?.direction === 'out' || String(props.signal?.name || '').includes('outbound')
})

const kindLabel = computed(() => isOutbound.value ? 'Outbound Email' : 'Inbound Email')

const accountName = computed(() => {
  return String(props.signal?.account_name || props.signal?.account_domain || '').trim()
})

const accountId = computed(() => {
  const domain = String(props.signal?.account_domain || '').trim()
  if (!domain) return ''
  return String(props.accountIdByDomain?.[domain] || '').trim()
})

const canOpenAccountDetail = computed(() => Boolean(accountId.value))

function openAccountDetail() {
  if (!canOpenAccountDetail.value) return
  router.push(`/accounts/${encodeURIComponent(accountId.value)}`)
}

const desc = computed(() => {
  const d = props.signal?.description
  return d && typeof d === 'object' ? d : {}
})

const fromDisplay = computed(() => String(desc.value.from_header || desc.value.from_email || '').trim() || '—')
const toDisplay = computed(() => String(desc.value.to || '').trim() || '—')
const ccDisplay = computed(() => String(desc.value.cc || '').trim())
const subjectDisplay = computed(() => String(desc.value.subject || '').trim() || '(no subject)')
const bodyDisplay = computed(() => String(desc.value.body || '').trim())

function formatTs(ts) {
  if (!ts) return ''
  try {
    return new Date(ts).toLocaleString()
  } catch {
    return String(ts)
  }
}

function openRun(runId) {
  const id = String(runId || '').trim()
  if (!id) return
  router.push(`/agent-runs/${encodeURIComponent(id)}`)
}

function agentIconById(agentId) {
  const aid = agentId ? String(agentId) : ''
  const a = aid ? props.agentById?.[aid] : null
  return {
    icon_name: a?.icon_name || null,
    icon_style: a?.icon_style || null,
    icon_color: a?.icon_color || null,
  }
}

function triggerAgentIcon(r) {
  const byId = agentIconById(r?.agent_id)
  if (byId?.icon_name) return byId
  return {
    icon_name: r?.agent_icon_name || null,
    icon_style: r?.agent_icon_style || null,
    icon_color: r?.agent_icon_color || null,
  }
}

function triggerRunTitle(r) {
  const tn = String(r?.trigger_name || '').trim()
  const an = String(r?.agent_name || '').trim()
  if (tn && an) return `${tn} → ${an}`
  return tn || an || ''
}

const triggerRuns = ref([])
const triggerRunsLoading = ref(false)
const triggerRunsError = ref('')

async function loadTriggerRuns(signalId) {
  triggerRuns.value = []
  triggerRunsError.value = ''
  if (!signalId) return
  if (Number(props.signal?.triggered_count || 0) <= 0) return
  triggerRunsLoading.value = true
  try {
    const resp = await apiFetch(`/signals/${signalId}/trigger_runs`)
    triggerRuns.value = Array.isArray(resp?.runs) ? resp.runs : []
  } catch (e) {
    triggerRunsError.value = e?.message || 'Failed'
    triggerRuns.value = []
  } finally {
    triggerRunsLoading.value = false
  }
}

watch(
  () => props.signal?.id,
  async (id) => {
    try {
      await loadTriggerRuns(id)
    } catch {}
  },
  { immediate: true },
)
</script>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  background: var(--backdrop);
  display: grid;
  place-items: center;
  padding: 18px;
  z-index: 60;
}

.modal {
  width: min(720px, 100%);
  max-height: calc(100vh - 36px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  box-shadow: 0 10px 30px var(--shadow);
  padding: 18px;
  position: relative;
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.x {
  position: absolute;
  top: 10px;
  right: 10px;
  border: none;
  background: transparent;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  color: var(--muted);
}

.top {
  display: flex;
  gap: 12px;
  align-items: center;
  padding-right: 28px;
}

.topLink {
  cursor: pointer;
  border-radius: 10px;
  padding: 8px;
  margin: -8px;
  transition: background-color 120ms ease;
}

.topLink:hover {
  background: var(--row-hover-bg);
}

.topLink:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px var(--focus-ring);
  background: var(--row-hover-bg);
}

.head {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.accountTitle {
  font-weight: 700;
  font-size: 20px;
}

.signalKindRow {
  display: flex;
  align-items: center;
  gap: 6px;
}

.gmailIcon {
  width: 14px;
  height: 14px;
  border-radius: 3px;
}

.signalKindLabel {
  font-size: 13px;
  color: var(--muted);
}

.directionBadge {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 999px;
  line-height: 1.4;
}

.directionBadge.in {
  background: #dbeafe;
  color: #1d4ed8;
}

.directionBadge.out {
  background: #dcfce7;
  color: #15803d;
}

.emailShell {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.addressBlock,
.messageBlock {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
}

.fieldRow {
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 12px;
  align-items: baseline;
}

.fieldKey {
  font-size: 11px;
  font-weight: 700;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding-top: 2px;
}

.fieldValue {
  min-width: 0;
  line-height: 1.4;
}

.emailAddr {
  font-weight: 600;
  word-break: break-all;
}

.subjectLine {
  font-size: 15px;
  font-weight: 600;
  color: var(--text);
  line-height: 1.3;
}

.bodyText {
  color: var(--text);
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 60px;
  font-size: 14px;
}

.muted {
  color: var(--muted);
}

.section {
  margin-top: 4px;
}

.label {
  font-weight: 700;
  margin-bottom: 8px;
}

.value {
  color: var(--text);
}

.triggerRuns {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.triggerRun {
  padding: 10px 12px;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--surface);
  cursor: pointer;
}

.triggerRunTop {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
}

.viewRunBtn {
  margin-left: auto;
}

.triggerNamePill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid var(--border);
  background: var(--surface);
  color: #7c3aed;
  font-size: 12px;
  line-height: 1;
}

.triggerNameText {
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

.triggerArrow {
  color: var(--muted);
  flex: none;
}

.triggerAgent {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.triggerAgentName {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 360px;
}

.footer {
  margin-top: 4px;
  border-top: 1px solid var(--border);
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.right {
  display: flex;
  gap: 8px;
}
</style>
